import React, { useState, useEffect } from 'react';
import './App.css';
import './styles/depression-mode.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { DepressionModeProvider } from './contexts/DepressionModeContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
// 旧LPはリダイレクトに置き換え済み - import削除
import ConsultationDetailPage from './pages/ConsultationDetailPage';
import AdvicePage from './components/AdvicePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SpecialCommercialLawPage from './pages/SpecialCommercialLawPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import FaqPage from './pages/FaqPage';
import HowToUsePage from './pages/HowToUsePage';
import TryDemoPage from './pages/TryDemoPage';
import MyPage from './pages/MyPage';
import MoodDetail from './pages/MoodDetail';
import BadgesPage from './pages/BadgesPage';
import ProfileEdit from './pages/ProfileEdit';
import FormPageV2 from './pages/FormPageV2';
import PlansPage from './pages/PlansPage';
import SavedRecipesPage from './pages/SavedRecipesPage';
import RecipeDetailPage from './pages/RecipeDetailPage';
import RecipeHubPage from './pages/RecipeHubPage';
import ConsultationHistoryPage from './pages/ConsultationHistoryPage';
import AccountStatusPage from './pages/AccountStatusPage';
import TabNavigation from './components/TabNavigation';
import ScrollToTop from './components/ScrollToTop';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import TsumugiSample from './components/TsumugiSample';
import TsumugiChat from './components/TsumugiChat';
import EmpathyFeed from './components/EmpathyFeed';
import EmpathyFeedAdmin from './components/admin/EmpathyFeedAdmin';
import AffiliateAdminPage from './pages/AffiliateAdminPage';
import AdminDashboard from './pages/AdminDashboard';
import AnnouncementAdminPage from './pages/AnnouncementAdminPage';
import IngredientInputPage from './pages/IngredientInputPage';
import FridgePhotoPage from './pages/FridgePhotoPage';
import FridgeRecipeResultPage from './pages/FridgeRecipeResultPage';
import UsagePolicy from './pages/UsagePolicy';
import FlyerPhotoPage from './pages/FlyerPhotoPage';
import FlyerRecipeResultPage from './pages/FlyerRecipeResultPage';
import IngredientCheckPage from './pages/IngredientCheckPage';
import IngredientCheckResultPage from './pages/IngredientCheckResultPage';
import SeasoningsSettingsPage from './pages/SeasoningsSettingsPage';
import SharedRecipePage from './pages/SharedRecipePage';
import LandingPageV2 from './pages/LandingPageV2';
import CampaignBanner from './components/CampaignBanner';
import CampaignModal, { shouldShowCampaignModal } from './components/CampaignModal';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import api from './api/api';
// import BadgeNotification from './components/BadgeNotification'; // TODO: バックエンドAPI実装後に有効化

// 開発環境のみDevToolPanelをインポート
const DevToolPanel = process.env.NODE_ENV === 'development'
  ? require('./components/DevToolPanel').default
  : null;

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const hasToken = !!localStorage.getItem('token');

  if (loading || (hasToken && !isAuthenticated)) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function AppContent() {
  const { getThemeClasses } = useTheme();
  const { isAuthenticated } = useAuth();
  const themeClasses = getThemeClasses();
  const location = useLocation();

  // キャンペーン状態管理
  const [campaign, setCampaign] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  // キャンペーン情報取得
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await api.get('/api/campaign/current');
        if (response.data && response.data.active) {
          setCampaign(response.data);
        }
      } catch (error) {
        // キャンペーン取得失敗時は静かに失敗（機能に影響なし）
        console.debug('Campaign info not available:', error.message);
      }
    };

    fetchCampaign();
  }, []);

  // ログイン後、初回表示時にモーダルを表示
  useEffect(() => {
    // ?showCampaignModal=true で強制表示（本番でも確認可能）
    const params = new URLSearchParams(window.location.search);
    if (params.get('showCampaignModal') === 'true') {
      localStorage.removeItem('campaignModalShown_testPlayer2026');
      setShowCampaignModal(true);
      return;
    }

    if (isAuthenticated && campaign && campaign.active && shouldShowCampaignModal()) {
      // 少し遅延させてページ遷移後に表示
      const timer = setTimeout(() => {
        setShowCampaignModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, campaign]);

  // キャンペーンモーダルの状態をグローバルに公開（TsumugiChatのおかえりプロンプトと連携）
  useEffect(() => {
    window.__campaignModalOpen = showCampaignModal;
  }, [showCampaignModal]);

  // つむぎチャット画面と管理画面ではフッターメニューを非表示
  const isChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  // LP、ログインページ、チャットページではバナーを非表示
  const shouldShowBanner = isAuthenticated &&
    campaign &&
    campaign.active &&
    !isChatPage &&
    location.pathname !== '/' &&
    location.pathname !== '/login' &&
    location.pathname !== '/register' &&
    location.pathname !== '/try';


  return (
    <div className={`min-h-screen ${themeClasses.pageBackground} flex flex-col relative`}>
      <ScrollToTop />
      <PWAInstallPrompt />
      {/* <BadgeNotification /> TODO: バックエンドAPI実装後に有効化 */}
      {/* 開発環境のみDevToolPanelを表示 */}
      {process.env.NODE_ENV === 'development' && DevToolPanel && <DevToolPanel />}

      {/* キャンペーンバナー（ログイン後、特定ページ以外で表示） */}
      {shouldShowBanner && <CampaignBanner campaign={campaign} />}

      {/* キャンペーンモーダル（初回ログイン時に表示） */}
      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        campaign={campaign}
      />

      <main className="flex-grow relative z-10">
        <Routes>
          <Route path="/" element={<LandingPageV2 />} />
          {/* 旧LPへのアクセスは新LPにリダイレクト */}
          <Route path="/lp-old" element={<Navigate to="/" replace />} />
          {/* 旧 /form は /form-v2 にリダイレクト */}
          <Route path="/form" element={<Navigate to="/form-v2" replace />} />
          <Route path="/form-v2" element={
            <PrivateRoute>
              <FormPageV2 />
            </PrivateRoute>
          } />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/onboarding" element={<Navigate to="/chat?newUser=true" replace />} />
          <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
          <Route path="/lp" element={<Navigate to="/" replace />} />
          <Route path="/advice" element={<AdvicePage />} />
          <Route path="/dashboard" element={<Navigate to="/mypage" replace />} />
          <Route path="/consultation/:id" element={
            <PrivateRoute>
              <ConsultationDetailPage />
            </PrivateRoute>
          } />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/commercial-law" element={<SpecialCommercialLawPage />} />
          <Route path="/usage-policy" element={<UsagePolicy />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/delete-account" element={
            <PrivateRoute>
              <DeleteAccountPage />
            </PrivateRoute>
          } />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/how-to-use" element={<HowToUsePage />} />
          <Route path="/try" element={<TryDemoPage />} />
          <Route path="/tsumugi" element={<TsumugiSample />} />
          <Route path="/chat" element={
            <PrivateRoute>
              <TsumugiChat />
            </PrivateRoute>
          } />
          <Route path="/chat/:sessionId" element={
            <PrivateRoute>
              <TsumugiChat />
            </PrivateRoute>
          } />
          <Route path="/empathy-feed" element={<EmpathyFeed />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/empathy-feed" element={<EmpathyFeedAdmin />} />
          <Route path="/admin/affiliate" element={<AffiliateAdminPage />} />
          <Route path="/admin/announcements" element={<AnnouncementAdminPage />} />
          <Route path="/mypage" element={
            <PrivateRoute>
              <MyPage />
            </PrivateRoute>
          } />
          <Route path="/mood-detail" element={
            <PrivateRoute>
              <MoodDetail />
            </PrivateRoute>
          } />
          <Route path="/badges" element={
            <PrivateRoute>
              <BadgesPage />
            </PrivateRoute>
          } />
          <Route path="/profile/edit" element={
            <PrivateRoute>
              <ProfileEdit />
            </PrivateRoute>
          } />
          <Route path="/plans" element={
            <PrivateRoute>
              <PlansPage />
            </PrivateRoute>
          } />
          <Route path="/recipe-hub" element={
            <PrivateRoute>
              <RecipeHubPage />
            </PrivateRoute>
          } />
          <Route path="/recipes" element={
            <PrivateRoute>
              <SavedRecipesPage />
            </PrivateRoute>
          } />
          <Route path="/recipes/:recipeId" element={
            <PrivateRoute>
              <RecipeDetailPage />
            </PrivateRoute>
          } />
          <Route path="/recipe/:recipeId" element={<SharedRecipePage />} />
          <Route path="/share/:shareId" element={<SharedRecipePage />} />
          <Route path="/consultation-history" element={
            <PrivateRoute>
              <ConsultationHistoryPage />
            </PrivateRoute>
          } />
          <Route path="/account-status" element={
            <PrivateRoute>
              <AccountStatusPage />
            </PrivateRoute>
          } />
          <Route path="/fridge-recipe" element={
            <PrivateRoute>
              <IngredientInputPage />
            </PrivateRoute>
          } />
          <Route path="/fridge-photo" element={
            <PrivateRoute>
              <FridgePhotoPage />
            </PrivateRoute>
          } />
          <Route path="/fridge-recipe-result" element={
            <PrivateRoute>
              <FridgeRecipeResultPage />
            </PrivateRoute>
          } />
          <Route path="/ingredient-check" element={
            <PrivateRoute>
              <IngredientCheckPage />
            </PrivateRoute>
          } />
          <Route path="/ingredient-check-result" element={
            <PrivateRoute>
              <IngredientCheckResultPage />
            </PrivateRoute>
          } />
          <Route path="/flyer-recipe" element={
            <PrivateRoute>
              <FlyerPhotoPage />
            </PrivateRoute>
          } />
          <Route path="/flyer-recipe-result" element={
            <PrivateRoute>
              <FlyerRecipeResultPage />
            </PrivateRoute>
          } />
          <Route path="/seasonings-settings" element={
            <PrivateRoute>
              <SeasoningsSettingsPage />
            </PrivateRoute>
          } />
        </Routes>
      </main>

      {/* タブナビゲーション（ログイン後のみ表示、チャット画面・管理画面・オンボーディング画面では非表示） */}
      {isAuthenticated && !isChatPage && !isAdminPage && <TabNavigation />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <DepressionModeProvider>
            <MaintenanceWrapper>
              <AppContent />
            </MaintenanceWrapper>
          </DepressionModeProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;